import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { updatePosition } from '@utils/valueContent/updatePosition';

import { InvisibleItemsPosition } from '@interfaces/invisibleItems';
import { IInvisibleItemsProps } from './InvisibleItems.types';

import SelectValue from '../SelectValue';
import {
	DEFAULT_POSITION,
	getInvisibleItemsClassName,
	getInvisibleItemsStyles,
	MAX_HEIGHT,
	MAX_WIDTH,
	PADDING_WIDTH,
} from './constantsе';

import './InvisibleItems.scss';

const InvisibleItems: React.FC<IInvisibleItemsProps> = ({
	invisibleItems,
	removeHandler,
	menuPlacement,
	maxHeight = MAX_HEIGHT,
	anchorRef = null,
	wrapperRef,
	isPortal = false,
	isDisabled = false,
}) => {
	const selectItemsRefs = useRef<any>([]);

	const [listOverflows, setListOverflows] = useState<Array<boolean>>(new Array(invisibleItems.length).fill(true));
	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
	const [position, setPosition] = useState<InvisibleItemsPosition>(DEFAULT_POSITION);

	const classNameWrapper = getInvisibleItemsClassName(menuPlacement);
	const wrapperStyles = getInvisibleItemsStyles(isPortal, position, maxHeight);

	const removeItem = useCallback((ids: Array<string>, categoryNames: string[]) => {
		removeHandler(ids, categoryNames);

		setListOverflows(new Array(invisibleItems.length - 1).fill(true));
	}, [invisibleItems.length, removeHandler]);

	useEffect(() => {
		const scrollWidth = (wrapperRef.current?.offsetWidth ?? 0) - (wrapperRef.current?.clientWidth ?? 0);

		setListOverflows((prev) =>
			prev.map((_, index) => {
				return (
					selectItemsRefs.current[index]?.current?.offsetWidth ?? 0
				) >= MAX_WIDTH - PADDING_WIDTH - scrollWidth;
			}),
		);
	}, [invisibleItems, wrapperRef]);

	useEffect(() => {
		if (!isPortal || !anchorRef?.current) return;

		let animationFrameId: number;

		const handlePosition = () => {
			if (animationFrameId) {
				cancelAnimationFrame(animationFrameId);
			}

			animationFrameId = requestAnimationFrame(() => {
				updatePosition({
					anchorElement: anchorRef.current,
					menuPlacement,
					maxHeight,
					setPosition,
					setPortalContainer,
				});
			});
		};

		handlePosition();

		window.addEventListener('scroll', handlePosition, true);
		window.addEventListener('resize', handlePosition);

		return () => {
			window.removeEventListener('scroll', handlePosition, true);
			window.removeEventListener('resize', handlePosition);
			cancelAnimationFrame(animationFrameId);
		};
	}, [anchorRef, isPortal, menuPlacement, maxHeight]);

	const content = (
		<div
			ref={wrapperRef}
			className={classNameWrapper}
			style={wrapperStyles}
		>
			{invisibleItems.map((option, index) => {
				selectItemsRefs.current[index] = selectItemsRefs.current[index] || React.createRef();

				const optionId = option.id ?? `unknown-id-${index}`;
				const optionCategoryName = option.categoryName ?? `unknown-category-${index}`;

				return (
					<SelectValue
						key={optionId + optionCategoryName}
						isDisabled={isDisabled}
						isOverflow={listOverflows[index]}
						option={option}
						removeHandler={removeItem}
						selectItemRef={selectItemsRefs.current[index]}
					/>
				);
			})}
		</div>
	);

	if (isPortal && portalContainer) {
		return createPortal(content, portalContainer);
	}

	return content;
};

export default InvisibleItems;
